import { ReactNode } from "react";

import { inferRouterInputs, inferRouterOutputs } from "@trpc/server";

import { AppRouterType } from "@/common/helpers/trpc";

export interface FilterConfigParams {
  label: string;
  selectGroup?: string;
  icon?: ReactNode;
}

export interface CustomPropertyFilterConfigParams extends FilterConfigParams {
  propertyId: string;
  icon: ReactNode;
}

export interface CustomPropertyMultiSelectFilterConfigParams extends CustomPropertyFilterConfigParams {
  choices: { id: string; body: string }[];
}

export type {
  BaseFilterInput,
  FilterConfig,
  FilterOperationOption,
  ValueComponentProps,
  FilterOption,
} from "@/components/FilterBox/types";

type RouterInputs = inferRouterInputs<AppRouterType>;
type RouterOutputs = inferRouterOutputs<AppRouterType>;
export type GetSupportContactsV2Output = RouterOutputs["getSupportContactsV2"];
export type AutocompleteOptionCampaignOutput = NonNullable<RouterOutputs["getCampaignsLabelAndValue"]>[number];
export type ApiFilterInputV2 = NonNullable<RouterInputs["createFilterList"]["filters"]>[number];
export type FilterInputV2 = ApiFilterInputV2 & { configId: string };
export type ContactSheetObject = NonNullable<NonNullable<GetSupportContactsV2Output>["items"]>[number];

export type CampaignFilterInput = Extract<FilterInputV2, { type: "campaign" }>;
export type FirstTransactionDateFilterInput = Extract<FilterInputV2, { type: "firstTransactionDate" }>;
export type LastTransactionDateFilterInput = Extract<FilterInputV2, { type: "lastTransactionDate" }>;
export type AnyTransactionDateFilterInput = Extract<FilterInputV2, { type: "anyTransactionDate" }>;
export type DonationDateFilterInput = Extract<FilterInputV2, { type: "donationDate" }>;
export type TotalAmountFilterInput = Extract<FilterInputV2, { type: "totalAmount" }>;
export type TotalTransactionFilterInput = Extract<FilterInputV2, { type: "totalTransaction" }>;
export type DonationFrequencyFilterInput = Extract<FilterInputV2, { type: "donationFrequency" }>;
export type MembershipFilterInput = Extract<FilterInputV2, { type: "membership" }>;
export type CompanyFilterInput = Extract<FilterInputV2, { type: "company" }>;
export type NoteFilterInput = Extract<FilterInputV2, { type: "note" }>;
export type LanguageFilterInput = Extract<FilterInputV2, { type: "language" }>;
export type StaticListFilterInput = Extract<FilterInputV2, { type: "staticList" }>;
export type ListFilterInput = Extract<FilterInputV2, { type: "list" }>;
export type CommunicationPreferenceFilterInput = Extract<FilterInputV2, { type: "communicationPreference" }>;
export type NewsletterFormSubscriptionFilterInput = Extract<FilterInputV2, { type: "newsletterFormSubscription" }>;
export type EndOfYearAudienceFilterInput = Extract<FilterInputV2, { type: "endOfYearAudience" }>;
export type RegionFilterInput = Extract<FilterInputV2, { type: "region" }>;
export type CountryFilterInput = Extract<FilterInputV2, { type: "country" }>;
export type AddressFilterInput = Extract<FilterInputV2, { type: "address" }>;
export type FirstNameFilterInput = Extract<FilterInputV2, { type: "firstName" }>;
export type LastNameFilterInput = Extract<FilterInputV2, { type: "lastName" }>;
export type EmailFilterInput = Extract<FilterInputV2, { type: "email" }>;
export type TotalEmailSentFilterInput = Extract<FilterInputV2, { type: "totalEmailSent" }>;
export type FirstEmailSentDateFilterInput = Extract<FilterInputV2, { type: "firstEmailSentDate" }>;
export type LastEmailSentDateFilterInput = Extract<FilterInputV2, { type: "lastEmailSentDate" }>;
export type MembershipStatusFilterInput = Extract<FilterInputV2, { type: "membershipStatus" }>;
export type MembershipTypeFilterInput = Extract<FilterInputV2, { type: "membershipType" }>;
export type JoinDateFilterInput = Extract<FilterInputV2, { type: "joinDate" }>;
export type ExpirationDateFilterInput = Extract<FilterInputV2, { type: "expirationDate" }>;
export type ValidityPeriodFilterInput = Extract<FilterInputV2, { type: "validityPeriod" }>;
export type AutoRenewFilterInput = Extract<FilterInputV2, { type: "autoRenew" }>;
export type HasMembershipFilterInput = Extract<FilterInputV2, { type: "hasMembership" }>;
export type CustomPropertyTextFilterInput = Extract<FilterInputV2, { type: "customPropertyText" }>;
export type CustomPropertyMultiSelectFilterInput = Extract<FilterInputV2, { type: "customPropertyMultiSelect" }>;
export type CustomPropertyDateFilterInput = Extract<FilterInputV2, { type: "customPropertyDate" }>;
export type CustomPropertyBooleanFilterInput = Extract<FilterInputV2, { type: "customPropertyBoolean" }>;
export type CrmCompanyFilterInput = Extract<FilterInputV2, { type: "crmCompany" }>;
export type CrmHouseholdFilterInput = Extract<FilterInputV2, { type: "crmHousehold" }>;
export type FilterOperation = FilterInputV2["operation"];
export type FilterCombinator = RouterInputs["createFilterList"]["combinator"];
export type FilterType = FilterInputV2["type"];
export enum ContactListType {
  Filter = "filter",
  Static = "static",
}

// Contact list types from tRPC
export type ContactListWithFilters = NonNullable<NonNullable<RouterOutputs["getStaticLists"]>["items"]>[number];
